import React, { useState, useEffect, useMemo } from "react";
import { LuX } from "react-icons/lu";
import DashboardBarChart, {
  formatCurrency,
  formatNumber,
  formatCompactCurrency,
  formatCompactNumber,
} from "./DashboardBarChart";

export default function DashboardChartModal({
  isOpen,
  onClose,
  title,
  type,
  data = [],
}) {
  // Metric selector state
  const [selectedMetric, setSelectedMetric] = useState(() => {
    if (type === "task") return "both";
    return "deal_price";
  });

  // Scale mode: "linear" | "log"
  const [scaleMode, setScaleMode] = useState("linear");

  // Display limit: "5" | "10" | "20" | "all"
  const [displayLimit, setDisplayLimit] = useState(() => {
    return data && data.length > 10 ? "10" : "all";
  });

  // Filter 0 values
  const [hideZeros, setHideZeros] = useState(() => {
    return Boolean(data && data.length > 5);
  });

  // Reset states when type or data changes or modal opens
  useEffect(() => {
    if (type === "task") {
      setSelectedMetric("both");
    } else {
      setSelectedMetric("deal_price");
    }
    setScaleMode("linear");
    if (data && data.length > 10) {
      setDisplayLimit("10");
    } else {
      setDisplayLimit("all");
    }
    if (data && data.length > 5) {
      setHideZeros(true);
    } else {
      setHideZeros(false);
    }
  }, [type, isOpen, data]);

  // Handle ESC key press to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Metric options configuration based on chart type
  const metricOptions = useMemo(() => {
    if (type === "organization" || type === "user") {
      return [
        { value: "deal_price", label: "Deal Price", isCurrency: true },
        { value: "leads", label: "Leads", isCurrency: false },
      ];
    }
    if (type === "task") {
      return [
        { value: "both", label: "Comparison (Assigned & Overdue)", isDual: true },
        { value: "assigned_tasks", label: "Assigned Tasks", isCurrency: false },
        { value: "overdue_tasks", label: "Overdue Tasks", isCurrency: false },
      ];
    }
    return [];
  }, [type]);

  const currentOption =
    metricOptions.find((opt) => opt.value === selectedMetric) || metricOptions[0];

  const categoryKey = type === "organization" ? "organization" : "user";
  const isDual = currentOption?.isDual || false;
  const isCurrency = currentOption?.isCurrency || false;
  const valueKey = isDual
    ? "assigned_tasks"
    : currentOption?.value || "deal_price";
  const valueLabel = isDual
    ? "Assigned Tasks"
    : currentOption?.label || "Deal Price";

  // Entity-specific empty state message
  const emptyMessage =
    type === "organization"
      ? "No organization data available for the selected filters."
      : type === "user"
      ? "No user data available for the selected filters."
      : "No task data available for the selected filters.";

  // Filtered and sorted data
  const processedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    let list = [...data];

    // Normalize property if needed (deal_price or price)
    list = list.map((item) => ({
      ...item,
      deal_price: item.deal_price ?? item.price ?? 0,
      assigned_tasks: item.assigned_tasks ?? item.tasks ?? 0,
      overdue_tasks: item.overdue_tasks ?? 0,
      leads: item.leads ?? 0,
    }));

    // Filter out zero values if requested
    if (hideZeros) {
      const nonZeroList = list.filter((item) => {
        const val = Number(item[valueKey]) || 0;
        const sec = isDual ? Number(item.overdue_tasks) || 0 : 0;
        return val > 0 || sec > 0;
      });
      // Only apply if at least one non-zero item exists
      if (nonZeroList.length > 0) {
        list = nonZeroList;
      }
    }

    // Sort descending by primary metric
    list.sort((a, b) => {
      const valA = Number(a[valueKey]) || 0;
      const valB = Number(b[valueKey]) || 0;
      return valB - valA;
    });

    // Apply limit
    if (displayLimit !== "all") {
      const limitNum = parseInt(displayLimit, 10);
      if (!isNaN(limitNum)) {
        list = list.slice(0, limitNum);
      }
    }

    return list;
  }, [data, valueKey, hideZeros, displayLimit, isDual]);

  // Summary stats for top strip
  const { totalSum, topEntity, avgVal } = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        totalSum: 0,
        topEntity: null,
        avgVal: 0,
      };
    }

    let sum = 0;
    let maxItem = null;
    let max = -Infinity;

    data.forEach((item) => {
      const val =
        Number(item[valueKey] ?? item.price ?? item.tasks ?? 0) || 0;
      sum += val;
      if (val > max) {
        max = val;
        maxItem = item;
      }
    });

    const avg = data.length > 0 ? sum / data.length : 0;

    return {
      totalSum: sum,
      topEntity: maxItem,
      avgVal: avg,
    };
  }, [data, valueKey]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="dashboard-modal-backdrop"
      onClick={handleBackdropClick}
      data-testid="chart-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="chart-modal-title"
    >
      <div className="dashboard-modal-card" data-testid="chart-modal-card">
        {/* Modal Header */}
        <div className="dashboard-modal-header">
          <div className="dashboard-modal-header-info">
            <h2
              id="chart-modal-title"
              className="dashboard-modal-title"
              data-testid="chart-modal-title"
            >
              {title}
            </h2>
            <span className="dashboard-modal-subtitle">
              {data.length > 0
                ? `Showing ${processedData.length} of ${data.length} ${
                    type === "organization"
                      ? "organizations"
                      : type === "user"
                      ? "users"
                      : "records"
                  }`
                : "No records found"}
            </span>
          </div>

          <button
            type="button"
            className="dashboard-modal-close-btn"
            onClick={onClose}
            aria-label="Close chart dialog"
            data-testid="chart-modal-close-btn"
          >
            <LuX />
          </button>
        </div>

        {/* Quick Stats Summary Strip */}
        {data.length > 0 && (
          <div className="dashboard-modal-stats-strip">
            <div className="chart-stat-chip">
              <span className="chart-stat-label">Total {valueLabel}:</span>
              <span className="chart-stat-value">
                {isCurrency
                  ? formatCurrency(totalSum)
                  : formatNumber(totalSum)}
              </span>
            </div>

            {topEntity && (
              <div className="chart-stat-chip highlight">
                <span className="chart-stat-label">Top Performer:</span>
                <span className="chart-stat-value">
                  {topEntity[categoryKey] || "N/A"} (
                  {isCurrency
                    ? formatCompactCurrency(
                        topEntity[valueKey] ?? topEntity.price ?? 0
                      )
                    : formatCompactNumber(
                        topEntity[valueKey] ?? topEntity.tasks ?? 0
                      )}
                  )
                </span>
              </div>
            )}

            <div className="chart-stat-chip">
              <span className="chart-stat-label">Average:</span>
              <span className="chart-stat-value">
                {isCurrency
                  ? formatCompactCurrency(avgVal)
                  : formatCompactNumber(Math.round(avgVal))}
              </span>
            </div>
          </div>
        )}

        {/* Modal Toolbar: Controls */}
        <div className="dashboard-modal-toolbar">
          {/* Metric Selector */}
          {metricOptions.length > 0 && (
            <div className="chart-toolbar-group">
              <label
                htmlFor="chart-metric-select"
                className="chart-metric-label"
              >
                Metric:
              </label>
              <select
                id="chart-metric-select"
                className="chart-metric-dropdown"
                value={selectedMetric}
                onChange={(e) => setSelectedMetric(e.target.value)}
                data-testid="chart-metric-select"
              >
                {metricOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Limit Selector */}
          {data.length > 5 && (
            <div className="chart-toolbar-group">
              <label htmlFor="chart-limit-select" className="chart-metric-label">
                Show:
              </label>
              <select
                id="chart-limit-select"
                className="chart-metric-dropdown"
                value={displayLimit}
                onChange={(e) => setDisplayLimit(e.target.value)}
              >
                <option value="5">Top 5</option>
                <option value="10">Top 10</option>
                <option value="20">Top 20</option>
                <option value="all">All ({data.length})</option>
              </select>
            </div>
          )}

          {/* Scale Mode Toggle: Normal vs Balanced */}
          <div className="chart-toolbar-group">
            <span className="chart-metric-label">Scale:</span>
            <div className="chart-toggle-pill-group">
              <button
                type="button"
                className={`chart-toggle-pill ${
                  scaleMode === "linear" ? "active" : ""
                }`}
                onClick={() => setScaleMode("linear")}
                title="Normal view: shows actual exact values"
              >
                Normal
              </button>
              <button
                type="button"
                className={`chart-toggle-pill ${
                  scaleMode === "log" ? "active" : ""
                }`}
                onClick={() => setScaleMode("log")}
                title="Balanced view: lifts smaller numbers so they are clearly visible"
              >
                Balanced
              </button>
            </div>
          </div>

          {/* Hide Zeroes Toggle */}
          {data.length > 5 && (
            <div className="chart-toolbar-group zero-toggle-group">
              <label className="chart-checkbox-label">
                <input
                  type="checkbox"
                  checked={hideZeros}
                  onChange={(e) => setHideZeros(e.target.checked)}
                  className="chart-checkbox"
                />
                <span>Hide Zeroes</span>
              </label>
            </div>
          )}
        </div>

        {/* Chart Body */}
        <div className="dashboard-modal-body">
          <DashboardBarChart
            data={processedData}
            categoryKey={categoryKey}
            valueKey={valueKey}
            valueLabel={valueLabel}
            isCurrency={isCurrency}
            isDual={isDual}
            secondaryKey="overdue_tasks"
            secondaryLabel="Overdue Tasks"
            emptyMessage={emptyMessage}
            scaleMode={scaleMode}
            chartOrientation="vertical"
            showValuesOnBars={true}
          />
        </div>
      </div>
    </div>
  );
}
